import React from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { usePublisherConfig } from '../../data/publisherConfigs';
import SelfServiceLayout from '../../components/self-service/SelfServiceLayout';
import SelfServiceLanding from '../../components/self-service/SelfServiceLanding';
import SelfServiceWizard from '../../components/self-service/SelfServiceWizard';
import { useAuth } from '../../components/self-service/auth/useAuth';
import { stepFromSlug, slugFromStep, type WizardStep } from '../../components/self-service/wizardSteps';
import { adoptDraftToken } from '../../components/self-service/useSelfServiceDraft';
import { buildRerunFromPayload } from '../../components/self-service/buildRerunFromPayload';
import { captureCodesFromUrl } from '../../components/self-service/codeCapture';
import { apiClient } from '../../lib/apiClient';
import type { SelfServiceSubmissionRow } from '../../types/selfService';
import * as analytics from '../../lib/analytics';
import { EVENTS } from '../../lib/analytics';

const SelfServicePage: React.FC = () => {
  const { slug, step: stepSlug } = useParams<{ slug: string; step?: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const { isAuthenticated, user, selectedAdvertiser } = useAuth();
  const { config, loading } = usePublisherConfig(slug);

  // "Run this Campaign Again" handoff — My Campaigns page passes pre-filled form
  // data + the original creative's storage path via route state. We capture
  // it once on mount so subsequent navigations within the wizard don't
  // retrigger the hydration.
  const rerunFrom = React.useMemo(() => {
    const rf = (location.state as { rerunFrom?: unknown } | null)?.rerunFrom;
    return rf ?? null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const rerunFromQueryId = searchParams.get('rerunFrom');
  const [queryRerunFrom, setQueryRerunFrom] = React.useState<unknown | null>(null);

  // CTV-520: email recap links land with `?rerunFrom={submissionId}` — hydrate
  // the same payload in-app navigation uses once the buyer is signed in.
  React.useEffect(() => {
    if (!rerunFromQueryId || rerunFrom || queryRerunFrom || !slug || !isAuthenticated) return;
    const advertiserId = selectedAdvertiser?.advertiserId;
    if (!advertiserId) return;

    let cancelled = false;
    void (async () => {
      try {
        const data = await apiClient.post<{ rows: SelfServiceSubmissionRow[] }>(
          '/submissions/list-by-advertiser',
          { advertiserId, stationSlug: slug },
        );
        const row = (data.rows ?? []).find((r) => r.id === rerunFromQueryId);
        if (!row || cancelled) return;
        setQueryRerunFrom(buildRerunFromPayload(row));
        const next = new URLSearchParams(searchParams);
        next.delete('rerunFrom');
        setSearchParams(next, { replace: true });
      } catch (e) {
        console.error('rerunFrom query hydration failed', e);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    rerunFromQueryId,
    rerunFrom,
    queryRerunFrom,
    slug,
    isAuthenticated,
    selectedAdvertiser?.advertiserId,
    searchParams,
    setSearchParams,
  ]);

  const effectiveRerunFrom = rerunFrom ?? queryRerunFrom;

  // Magic-link resume: ?resume=<draftToken>[&advertiser=<id>] hands the user
  // back into their unfinished draft and (when present) the advertiser seat
  // it was bound to. Adopt the token into the matching localStorage slot and
  // strip the query params so they don't get bookmarked/shared.
  // CTV-554: scoped per (slug, advertiser, user). For a signed-in resume the
  // userId comes from useAuth; for an anonymous resume the token lands in
  // the anon.anon slot and migrates on sign-in via the hook's promotion.
  React.useEffect(() => {
    if (!slug) return;
    const incoming = searchParams.get('resume');
    if (!incoming) return;
    const incomingAdvertiser = searchParams.get('advertiser');
    adoptDraftToken(slug, incoming, incomingAdvertiser ?? null, user?.id ?? null);
    const next = new URLSearchParams(searchParams);
    next.delete('resume');
    next.delete('advertiser');
    setSearchParams(next, { replace: true });
  }, [slug, searchParams, setSearchParams, user?.id]);

  // Capture ?ref= / ?promo= into per-publisher localStorage and strip them
  // from the URL. Runs once per slug change. Last-touch wins, 30-day TTL.
  React.useEffect(() => {
    if (!slug) return;
    captureCodesFromUrl(slug);
  }, [slug]);

  // URL drives whether we're on the landing page or inside the wizard.
  // /self-service/:slug                    → landing
  // /self-service/:slug/start              → wizard, redirected to /start/business
  // /self-service/:slug/start/:stepSlug    → wizard at that step
  const inWizard = window.location.pathname.includes('/start');
  const currentStep: WizardStep = stepFromSlug(stepSlug) ?? 1;

  // Canonicalize /start to /start/business so URLs are always step-explicit.
  React.useEffect(() => {
    if (inWizard && !stepSlug && slug) {
      navigate(`/${slug}/start/${slugFromStep(1)}`, { replace: true });
    }
  }, [inWizard, stepSlug, slug, navigate]);

  // CTV-471 note: the "already-signed-in users never see /start/verify"
  // redirect used to live here, keyed off a ref latched at THIS component's
  // mount. The landing and wizard are sibling routes rendering this same
  // component, so the ref survived a mid-session sign-out and kept bouncing
  // the (now signed-out) buyer from the Verify step back to step 2 in an
  // endless 3→2 loop. The redirect now lives in SelfServiceWizard, driven by
  // the same `skipVerify` latch that hides the step — one latch, no desync.

  React.useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [inWizard]);

  // CTV-468 analytics: tag every event with the station, then emit the funnel
  // entry/step events. station_viewed fires on the landing; wizard_step_viewed
  // drives the PostHog drop-off funnel (business → campaign → video → verify →
  // review). wizard_started is fired from the Get Started handler below.
  const stationSlug = config?.slug ?? null;
  React.useEffect(() => {
    if (slug) analytics.registerContext({ station_slug: slug });
  }, [slug]);

  React.useEffect(() => {
    if (stationSlug && !inWizard) {
      analytics.track(EVENTS.STATION_VIEWED, { station_slug: stationSlug });
    }
  }, [stationSlug, inWizard]);

  React.useEffect(() => {
    if (stationSlug && inWizard) {
      analytics.track(EVENTS.WIZARD_STEP_VIEWED, {
        step: slugFromStep(currentStep),
        step_number: currentStep,
        station_slug: stationSlug,
      });
    }
  }, [stationSlug, inWizard, currentStep]);

  if (loading) {
    return <div className="min-h-screen bg-white flex items-center justify-center text-slate-500">Loading…</div>;
  }
  if (!config) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-slate-900 mb-4">Page Not Found</h1>
          <p className="text-slate-600 text-lg">
            This self-service portal doesn't exist or is no longer active.
          </p>
        </div>
      </div>
    );
  }

  const goToStep = (next: WizardStep) => {
    navigate(`/${config.slug}/start/${slugFromStep(next)}`);
  };

  return (
    <SelfServiceLayout config={config}>
      {inWizard ? (
        <SelfServiceWizard
          config={config}
          currentStep={currentStep}
          onStepChange={goToStep}
          onExit={() => navigate(`/${config.slug}`)}
          rerunFrom={effectiveRerunFrom as never}
        />
      ) : (
        <SelfServiceLanding
          config={config}
          onGetStarted={() => {
            analytics.track(EVENTS.WIZARD_STARTED, { station_slug: config.slug });
            navigate(`/${config.slug}/start/${slugFromStep(1)}`);
          }}
        />
      )}
    </SelfServiceLayout>
  );
};

export default SelfServicePage;
